import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token, refreshUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided');
      return;
    }
    
    verifyPayment();
  }, [sessionId]);
  
  const verifyPayment = async () => {
    try {
      // Use public endpoint that doesn't require authentication
      const response = await axios.get(
        `${API_BASE_URL}/payments/verify-session/${sessionId}`
      );
      
      if (response.data.success) {
        setStatus('success');
        setPaymentData(response.data.data);
        
        // Refresh user data to get updated plan
        if (isAuthenticated && refreshUser) {
          console.log('Refreshing user data after payment...');
          const updatedUser = await refreshUser();
          console.log('Updated user plan:', updatedUser?.plan_type);
        }
      } else {
        setStatus('error');
        setError(response.data.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to verify payment. Please contact support.');
    }
  };
  
  const getPlanDisplayName = (planName) => {
    if (!planName) return 'Unknown';
    return planName.charAt(0).toUpperCase() + planName.slice(1);
  };
  
  const handleGoToDashboard = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      // If opened in new tab and not logged in, redirect to login
      navigate('/login');
    }
  };
  
  const handleCloseTab = () => {
    // Try to close the tab (works if opened via window.open)
    window.close();
    // If can't close, show message
    setTimeout(() => {
      setError('You can close this tab and return to the original window.');
    }, 500);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-20">
        <div className="max-w-lg mx-auto px-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-yellow-500 animate-spin" />
                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                  Verifying Payment...
                </h1>
                <p className="text-gray-600">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                  Payment Successful!
                </h1>
                <p className="text-gray-600 mb-6">
                  {paymentData?.already_processed 
                    ? 'Your payment was already processed.'
                    : `Thank you for upgrading to the ${getPlanDisplayName(paymentData?.plan_name)} plan.`
                  }
                </p>
                
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <p className="text-green-700 font-medium">
                    Your subscription is now active!
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    You will be billed monthly until you cancel.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={handleGoToDashboard}
                    className="w-full px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
                    data-testid="go-to-dashboard-btn"
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
                  </button>
                  
                  <button
                    onClick={handleCloseTab}
                    className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold transition-colors"
                    data-testid="close-tab-btn"
                  >
                    Close This Tab
                  </button>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    If you opened this in a new tab, you can close it and refresh your original page.
                  </p>
                </div>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                  Payment Verification Issue
                </h1>
                <p className="text-gray-600 mb-6">
                  {error}
                </p>
                <div className="space-y-3">
                  <button
                    onClick={verifyPayment}
                    className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleGoToDashboard}
                    className="w-full px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
